import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { RequestService } from 'src/app/services/request.service';
import { TranslateService } from '@ngx-translate/core';
import { ShareDataService } from 'src/app/services/share-data.service';
import { AddTrapComponent } from './add-trap/add-trap.component';
import { MatDialog } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { Chart, ChartOptions } from 'chart.js';

declare var google: any;

@Component({
  selector: 'app-manage-dacus',
  templateUrl: './manage-dacus.component.html',
  styleUrls: ['./manage-dacus.component.scss']
})
export class ManageDacusComponent implements OnInit {
  availablePlots: any[] = [];
  plotCode: any[] = [];
  type: string = '';
  plotCodes: any = [];
  traps: any[] = [];
  filteredTraps: any[] = [];
  expandedPanelIndex: number = -1;
  types: any = [
    'DACUS.FOOD',
    'DACUS.PHEROMONE',
    'DACUS.CHROMATIC',
  ];
  startDate: any = '';
  endDate: any = '';
  translatedTypes: any = {};

  pieChartOptions: ChartOptions<'pie'> = {
    responsive: true,
  };
  pieChartLegend = true;
  pieChartLabels: string[] = [];
  pieChartData: any[] = [];

  barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
  };
  barChartLegend = true;
  barChartLabels: string[] = ['Females', 'Males', 'Total'];
  barChartData: any[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private req: RequestService<any>,
    private shareDataService: ShareDataService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.translate.get(this.types).subscribe((translations) => {
      this.translatedTypes = translations;
    });
    this.shareDataService.getData<any>('plots').subscribe((res: any) => {
      if (res) {
        this.availablePlots = res.filter((plot: any) =>
          plot.crop_info.some((crop: any) => ((crop.alt_crop_type.includes('ΕΛΑΙΩΝΕΣ') || (crop.crop_type.includes('ΕΛΑΙΩΝΕΣ')))
          )
          ))
        this.availablePlots.forEach((plot: any) => {
          this.plotCodes.push([plot.plot_code, plot.location, plot._id]);
        });

        this.refreshTraps();
      }
    });
  }

  flattenTraps(data: any[]): any[] {
    return data.reduce((acc: any[], plot: any) => {
      const matchedPlot = this.plotCodes.find((code: any) => code[2] === plot.plot_id);

      const updatedTraps = plot.traps
        .map((trap: any) => ({
          ...trap,
          plotCode: matchedPlot ? matchedPlot[0] : '',
          location: matchedPlot ? matchedPlot[1] : '',
          plot_id: plot.plot_id,
          id: plot._id
        }))
        .filter((trap: any) => trap.plotCode !== '' && trap.location !== '');

      return [...acc, ...updatedTraps];
    }, []);
  }

  filterTraps(): void {
    this.filteredTraps = this.traps.filter((trap) => {
      const matchesPlot = this.plotCode.length > 0
        ? this.plotCode.includes(trap.plotCode)
        : true;

      const matchesType = this.type
        ? trap.trap_type === this.type
        : true;

      const matchesDate = this.startDate && this.endDate
        ? trap.placement_date >= this.startDate && trap.placement_date <= this.endDate
        : true;

      return matchesPlot && matchesType && matchesDate;
    });

    this.filteredTraps = [...this.filteredTraps];
  }

  updateStartDate(event: any): void {
    if (event) {
      this.startDate = this.formatDate(event.value);
    }
  }

  updateEndDate(event: any): void {
    if (event) {
      this.endDate = this.formatDate(event.value);
      this.filterTraps()
    }
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  resetFilters() {
    this.plotCode = [];
    this.startDate = null;
    this.endDate = null;
    this.type = '';
  
    this.filteredTraps = [...this.traps];
    this.cdr.detectChanges(); 
  
    setTimeout(() => {
      this.initMap();
      this.updatePieChart();
      this.updateBarChart();
    }, 100);
  }
  

  togglePanel(index: number): void {
    this.expandedPanelIndex = this.expandedPanelIndex === index ? -1 : index;
  }

  addTrap(): void {
    const dialogRef = this.dialog.open(AddTrapComponent, {
      width: '90%',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: 'scrollable-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.refreshTraps();
    });
  }

  refreshTraps() {
    this.req.getTraps().subscribe((res) => {
      this.traps = this.flattenTraps(res);
      this.filteredTraps = [...this.traps];
      this.cdr.detectChanges();

      this.initMap();
      this.updatePieChart();
      this.updateBarChart();
    });
  }

  initMap(): void {
    const mapElement = document.getElementById("map");
    if (!mapElement) return;
  
    // Clear previous map instance
    mapElement.innerHTML = ""; 
  
    if (this.filteredTraps.length > 0) {
      const firstTrap = this.filteredTraps[0];
  
      const map = new google.maps.Map(mapElement, {
        zoom: 15,
        center: { lat: firstTrap.coordinates[0].lat, lng: firstTrap.coordinates[0].lon },
        mapTypeId: google.maps.MapTypeId.SATELLITE,
        mapTypeControl: false,
        streetViewControl: false
      });
  
      this.filteredTraps.forEach(trap => {
        if (trap.coordinates?.length) {
          const marker = new google.maps.Marker({
            position: { lat: trap.coordinates[0].lat, lng: trap.coordinates[0].lon },
            map: map,
            title: `${trap.trap_num}`
          });
  
          const infoWindow = new google.maps.InfoWindow({
            content: `<div class="info-window-content" style="overflow: hidden; min-width: 100px; min-height: 30px">
                        <p style="font-size: medium; font-weight: semibold;">${trap.trap_type}</p>
                      </div>`
          });
  
          marker.addListener('click', () => {
            infoWindow.open(map, marker);
          });
        }
      });
    }
  }  

  updatePieChart(): void {
    const trapCounts: { [key: string]: number } = {};
    this.traps.forEach(trap => {
      trapCounts[trap.trap_type] = (trapCounts[trap.trap_type] || 0) + 1;
    });

    this.pieChartLabels = Object.keys(trapCounts);
    this.pieChartData = [{
      data: Object.values(trapCounts),
      backgroundColor: [
        'rgba(47, 64, 55, 0.6)',
        'rgba(107, 143, 113, 0.6)',
        'rgba(184, 224, 198, 0.6)'
      ],
      borderColor: [
        'rgba(47, 64, 55, 1)',
        'rgba(107, 143, 113, 1)',
        'rgba(184, 224, 198, 1)'
      ],
      borderWidth: 1
    }];
  }

  updateBarChart(): void {
    let totalFemales = 0, totalMales = 0, totalFlies = 0;

    this.traps.forEach(trap => {
      trap.measurements.forEach((measurement: any) => {
        totalFemales += measurement.fly_female;
        totalMales += measurement.fly_male;
        totalFlies += measurement.fly_total;
      });
    });

    this.translate.get([
      'DACUS.FEMALE_FLY',
      'DACUS.MALE_FLY',
      'DACUS.TOTAL'
    ]).subscribe(translations => {
      this.barChartLabels = [
        translations['DACUS.FEMALE_FLY'],
        translations['DACUS.MALE_FLY'],
        translations['DACUS.TOTAL']
      ];

      const totalColor = 'rgba(184, 224, 198, 0.8)';

      this.barChartData = [{
        label: `${translations['DACUS.TOTAL']}: ${totalFlies}`,
        data: [totalFemales, totalMales, totalFlies],
        backgroundColor: [
          'rgba(47, 64, 55, 0.8)',
          'rgba(107, 143, 113, 0.8)',
          totalColor
        ],
        borderColor: [
          'rgba(47, 64, 55, 1)',
          'rgba(107, 143, 113, 1)',
          'rgba(184, 224, 198, 1)'
        ],
        borderWidth: 0
      }];

      this.barChartOptions = {
        plugins: {
          legend: {
            labels: {
              generateLabels: (chart) => {
                let labels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                labels.forEach(label => {
                  if (label.text.startsWith(translations['DACUS.TOTAL'])) {
                    label.fillStyle = totalColor;
                  }
                });
                return labels;
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                const datasetIndex = tooltipItem.dataIndex;

                if (datasetIndex === 0) {
                  return `${translations['DACUS.FEMALE_FLY']}: ${totalFemales}`;
                } else if (datasetIndex === 1) {
                  return `${translations['DACUS.MALE_FLY']}: ${totalMales}`;
                } else {
                  return `${translations['DACUS.TOTAL']}: ${totalFlies}`;
                }
              }
            }
          }
        }
      };
    });
  }

  confirmDelete(trap: any) {
    const title = 'DACUS.DELETE_TITLE';
    const yesDelete = 'PRODUCT.YES_DELETE';
    const deletedTitle = 'PRODUCT.DELETED_TITLE';
    const deletedText = 'DACUS.DELETED_TEXT';
    const cancel = 'PRODUCT.CANCEL';
    this.translate.get([title, yesDelete, yesDelete, cancel, deletedTitle, deletedText])
      .subscribe(translations => {
        Swal.fire({
          title: translations[title],
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: 'rgb(147, 163, 160)',
          confirmButtonText: translations[yesDelete],
          cancelButtonText: translations[cancel],
          showClass: {
            popup: 'animate__animated animate__fadeInDown'
          },
          hideClass: {
            popup: 'animate__animated animate__fadeOutUp'
          },
        }).then((result) => {
          if (result.isConfirmed) {
            Swal.fire({
              title: translations[deletedTitle],
              text: translations[deletedText],
              icon: 'success',
              showClass: {
                popup: 'animate__animated animate__fadeInDown'
              },
              hideClass: {
                popup: 'animate__animated animate__fadeOutUp'
              },
              timer: 3000
            });
            this.req.deleteTrap(trap.id).subscribe(() => {
              this.refreshTraps();
            });
          }
        });
      });
  }

  editTrap(trap: any) {
    const dialogRef = this.dialog.open(AddTrapComponent, {
      width: '90%',
      maxHeight: '90vh',
      autoFocus: false,
      panelClass: 'scrollable-dialog',
      data: trap
    });
    dialogRef.afterClosed().subscribe((result) => {
      this.refreshTraps();
    });
  }
}